import {
  createIntl,
  createIntlCache,
  IntlShape,
  MessageFormatElement,
} from 'react-intl';
import { z } from 'zod';

import enMessages from '../compiled-locale/en-GB.json';

import { safeGetItem } from '~/utils/safeStorage';

type LocaleMessages = Record<string, MessageFormatElement[]>;

const DEFAULT_FALLBACK_LANG = 'en';
const cache = createIntlCache();

// Runtime cache of loaded locale messages — seeded with English
const localeCache: Record<string, LocaleMessages> = {
  en: enMessages as unknown as LocaleMessages,
};

// Dynamic import map — Vite code-splits each into its own chunk
const localeImportMap: Record<
  string,
  () => Promise<{ default: LocaleMessages }>
> = {
  ar: () => import('../compiled-locale/ar.json'),
  ca: () => import('../compiled-locale/ca.json'),
  de: () => import('../compiled-locale/de.json'),
  el: () => import('../compiled-locale/el.json'),
  es: () => import('../compiled-locale/es.json'),
  fr: () => import('../compiled-locale/fr.json'),
  id: () => import('../compiled-locale/id.json'),
  ja: () => import('../compiled-locale/ja.json'),
  km: () => import('../compiled-locale/km.json'),
  ms: () => import('../compiled-locale/ms.json'),
  my: () => import('../compiled-locale/my.json'),
  nl: () => import('../compiled-locale/nl.json'),
  ro: () => import('../compiled-locale/ro.json'),
  th: () => import('../compiled-locale/th.json'),
  tl: () => import('../compiled-locale/tl.json'),
  vi: () => import('../compiled-locale/vi.json'),
  zh: () => import('../compiled-locale/zh.json'),
  pt: () => import('../compiled-locale/pt.json'),
  ko: () => import('../compiled-locale/ko.json'),
  bn: () => import('../compiled-locale/bn.json'),
  ta: () => import('../compiled-locale/ta.json'),
  da: () => import('../compiled-locale/da.json'),
  it: () => import('../compiled-locale/it.json'),
  ne: () => import('../compiled-locale/ne.json'),
  hi: () => import('../compiled-locale/hi.json'),
};

/**
 * Asynchronously load a locale and store it in the cache.
 * If already cached, resolves immediately.
 */
export async function loadLocale(code: string): Promise<void> {
  if (localeCache[code]) return;
  const importer = localeImportMap[code];
  if (!importer) return;
  try {
    const mod = await importer();
    localeCache[code] = mod.default;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Failed to load locale "${code}":`, err);
  }
}

/**
 * Check whether a locale's messages are already in the cache.
 */
export function isLocaleLoaded(code: string): boolean {
  return code in localeCache;
}

const getCurrentLocale = () => {
  // Web Workers do not support local storage, and
  // this file is read by the worker to generate PDFs from react-pdf
  if (typeof localStorage !== 'undefined') {
    const raw = safeGetItem('lang');
    const language = JSON.parse(raw ?? 'null');
    return language ? language.code : DEFAULT_FALLBACK_LANG;
  }
  return DEFAULT_FALLBACK_LANG;
};

/**
 * Synchronous — reads from the locale cache.
 * If the requested locale is not yet loaded, falls back to English.
 */
export const getCurrentIntl = (): IntlShape => {
  const locale = getCurrentLocale();
  const messages =
    localeCache[locale] ?? localeCache[DEFAULT_FALLBACK_LANG];
  return createIntl(
    { locale, messages, defaultLocale: DEFAULT_FALLBACK_LANG },
    cache,
  );
};

/**
 * Custom Zod error map that translates all default validation messages.
 * Called at validation time, so it picks up the current locale.
 */
const zodErrorMap: z.ZodErrorMap = (issue, ctx) => {
  const intl = getCurrentIntl();

  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      if (issue.received === 'undefined' || issue.received === 'null') {
        return {
          message: intl.formatMessage({ defaultMessage: 'Required' }),
        };
      }
      return {
        message: intl.formatMessage(
          { defaultMessage: 'Expected {expected}, received {received}' },
          { expected: issue.expected, received: issue.received },
        ),
      };

    case z.ZodIssueCode.too_small:
      if (issue.minimum === 1 && (issue.type === 'string' || issue.type === 'array')) {
        return {
          message: intl.formatMessage({ defaultMessage: 'Required' }),
        };
      }
      return { message: ctx.defaultError };

    case z.ZodIssueCode.invalid_string:
      if (issue.validation === 'email') {
        return {
          message: intl.formatMessage({
            defaultMessage: 'Invalid email address',
          }),
        };
      }
      if (issue.validation === 'url') {
        return {
          message: intl.formatMessage({ defaultMessage: 'Invalid URL' }),
        };
      }
      return { message: ctx.defaultError };

    default:
      return { message: ctx.defaultError };
  }
};

z.setErrorMap(zodErrorMap);
